'use client';

import { useContext, useState } from 'react';
import Link from 'next/link';
import Image from '@components/common/ImageFallback';
import { DataContextCart } from '@root/src/contextcart';

interface ProductCardProps {
  id: string;
  name: string;
  slug: string;
  image: string;
  price: number;
  discount?: number;
  onPromotion?: boolean;
  totalQuantity?: number;
  onAddToCart?: (id: string) => void;
}

export default function ProductCard({
  id,
  name,
  slug,
  image,
  price,
  discount = 0,
  onPromotion = false,
  totalQuantity = 0,
  onAddToCart,
}: ProductCardProps) {
  const [added, setAdded] = useState(false);
  const contextCart: any = useContext(DataContextCart);
  const { dataCart = [] } = contextCart || {};

  const discountedPrice = discount > 0 ? price - (price * discount) / 100 : price;
  const showDiscount = onPromotion && discount > 0;
  const normalizedTotalQuantity = Number(totalQuantity || 0);
  const isOutOfStock = normalizedTotalQuantity <= 0;
  const cartQuantity = Number(
    (dataCart || []).find((item: any) => String(item.productId) === String(id))?.quantity || 0
  );
  const isAtStockLimit = !isOutOfStock && cartQuantity >= normalizedTotalQuantity;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOutOfStock || isAtStockLimit) return;
    onAddToCart?.(id);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  return (
    <Link
      href={`/${slug}`}
      className="group block bg-white rounded-2xl border border-[#EDE6D8] overflow-hidden transition-shadow duration-300 hover:shadow-[0_8px_32px_rgba(34,58,39,0.12)]"
    >
      {/* Product Image */}
      <div className="relative overflow-hidden bg-[#F5EFE4] aspect-square">
        <Image
          src={image}
          alt={name}
          fill
          className="object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {showDiscount && (
          <div className="absolute top-2 right-2 bg-[#223A27] text-white px-2 py-0.5 rounded-full text-[10px] font-semibold">
            -{discount}%
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-3">
        <h3 className="text-[13px] font-medium text-[#2C2C2C] leading-5 line-clamp-2 min-h-[2.5rem] mb-2">
          {name}
        </h3>
        <div className="flex items-center justify-between gap-1">
          <div className="flex flex-col">
            <span className="text-[13px] font-semibold text-[#2C2C2C]">
              {discountedPrice.toLocaleString('vi-VN')}đ
            </span>
            {showDiscount && (
              <span className="text-[11px] text-gray-400 line-through leading-none">
                {price.toLocaleString('vi-VN')}đ
              </span>
            )}
            <span
              className={`mt-1 text-[11px] font-semibold leading-none ${
                isOutOfStock ? 'text-[#A14B4B]' : 'text-[#7D735F]'
              }`}
            >
              {isOutOfStock
                ? 'Liên hệ 0931 338 566'
                : isAtStockLimit
                  ? `Đã thêm tối đa: ${normalizedTotalQuantity}`
                  : `Còn lại: ${normalizedTotalQuantity}`}
            </span>
          </div>

          <button
            onClick={handleAddToCart}
            aria-label="Thêm vào giỏ hàng"
            disabled={isOutOfStock || isAtStockLimit}
            className={`
              mr-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border
              sm:h-9 sm:w-9
              transition-all duration-200
              ${isOutOfStock || isAtStockLimit
                ? 'border-[#D8D0C3] bg-[#F6F2EB] text-[#B7AF9F] cursor-not-allowed'
                : added
                ? 'bg-[#223A27] border-[#223A27] text-white scale-95'
                : 'border-[#C8BFB0] text-[#5A5A4A] hover:bg-[#223A27] hover:border-[#223A27] hover:text-white'
              }
            `}
          >
            {added ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </Link>
  );
}
